<script lang="ts">
  import { STORE } from "../../state.svelte";
  import { shortAddress, timeAgo } from "../../utils";
  import CandleChart from "./CandleChart.svelte";
  import {
    type Candle,
    type IndicatorConfig,
    type IndicatorResult,
    type IndicatorType,
    INDICATOR_DEFAULTS,
    calculateIndicator,
  } from "../utils/indicators";
  import {
    SailfishApi,
    SailfishTier,
    type Trade,
    PoolType,
  } from "sailfish-sdk";

  const SLOT_DURATION_MS = 400;
  const MAX_SLOT_RANGE = 10_000_000;

  const ALL_POOL_TYPES = [
    PoolType.PumpSwapAmm, PoolType.RaydiumLaunchpad, PoolType.MeteoraDyn,
    PoolType.MeteoraDynV2, PoolType.PumpFunAmm, PoolType.RaydiumAmm,
    PoolType.RaydiumCpmm, PoolType.RaydiumClmm,
  ];

  const api = new SailfishApi({
    tier: SailfishTier.demo({ apiKey: STORE.bot.config.sailfish_api }),
  });

  let startDate = $state("");
  let endDate = $state("");
  let currentSlot = $state(0);
  let slotFetchTime = $state(0);
  let poolAddress = $state("3ucNos4NbumPLZNWztqGHNFFgkHeRMBQAVemeeomsUxv");
  let isLoading = $state(false);
  let currentFetchingRange: { lower_tick: number; upper_tick: number } | null = $state(null);
  let fetchProgress = $state(0);
  let fetchTotal = $state(0);
  let errorMsg = $state("");
  let trades: Trade[] = $state([]);

  const PAGE_SIZE = 50;
  let tradePage = $state(0);
  let sortedTrades = $derived([...trades].sort((a, b) => b.index.tick - a.index.tick));
  let totalTradePages = $derived(Math.max(1, Math.ceil(sortedTrades.length / PAGE_SIZE)));
  let pagedTrades = $derived(sortedTrades.slice(tradePage * PAGE_SIZE, (tradePage + 1) * PAGE_SIZE));

  const INTERVALS = [
    { label: "1s", ms: 1_000 }, { label: "5s", ms: 5_000 }, { label: "15s", ms: 15_000 },
    { label: "1m", ms: 60_000 }, { label: "5m", ms: 300_000 }, { label: "15m", ms: 900_000 },
    { label: "1h", ms: 3_600_000 },
  ];
  let candleInterval = $state(900_000);

  let activeIndicators: IndicatorConfig[] = $state([]);
  const AVAILABLE_INDICATORS: { type: IndicatorType; label: string }[] = [
    { type: "SMA", label: "SMA" }, { type: "EMA", label: "EMA" },
    { type: "RSI", label: "RSI" }, { type: "MACD", label: "MACD" }, { type: "BB", label: "BB" },
  ];

  function toDatetimeLocal(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function dateToSlot(dateStr: string): number {
    if (!dateStr || !currentSlot || !slotFetchTime) return 0;
    const targetMs = new Date(dateStr).getTime();
    return Math.max(0, currentSlot - Math.floor((slotFetchTime - targetMs) / SLOT_DURATION_MS));
  }

  let computedStartSlot = $derived(dateToSlot(startDate));
  let computedEndSlot = $derived(dateToSlot(endDate));
  let slotRange = $derived(computedEndSlot - computedStartSlot);
  let rangeExceeded = $derived(slotRange > MAX_SLOT_RANGE);

  $effect(() => {
    (async () => {
      try {
        currentSlot = await api.fetchLatestBlock();
        slotFetchTime = Date.now();
        const now = new Date();
        endDate = toDatetimeLocal(now);
        startDate = toDatetimeLocal(new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000));
      } catch (e: any) { errorMsg = e.message ?? "Failed to fetch latest block"; }
    })();
  });

  let historicCandles = $derived.by<Candle[]>(() => {
    if (trades.length === 0 || !currentSlot || !slotFetchTime) return [];
    const sorted = [...trades].sort((a, b) => a.index.tick - b.index.tick);
    const candleMap = new Map<number, Candle>();
    for (const trade of sorted) {
      const price = parseFloat(trade.price) || 0;
      const volume = Math.abs(parseFloat(trade.quote_amount) || 0);
      if (price === 0) continue;
      const tradeMs = slotFetchTime - (currentSlot - trade.index.tick) * SLOT_DURATION_MS;
      const bucket = Math.floor(tradeMs / candleInterval) * candleInterval;
      const existing = candleMap.get(bucket);
      if (existing) {
        existing.high = Math.max(existing.high, price);
        existing.low = Math.min(existing.low, price);
        existing.close = price;
        existing.volume += volume;
        existing.trades += 1;
      } else {
        candleMap.set(bucket, { timestamp: bucket, open: price, high: price, low: price, close: price, volume, trades: 1 });
      }
    }
    return Array.from(candleMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  });

  let indicatorResults = $derived.by<IndicatorResult[]>(() => {
    if (historicCandles.length < 2) return [];
    return activeIndicators.filter((i) => i.enabled).map((config) => calculateIndicator(historicCandles, config));
  });

  function toggleIndicator(type: IndicatorType) {
    const idx = activeIndicators.findIndex((i) => i.type === type);
    if (idx >= 0) { activeIndicators[idx].enabled = !activeIndicators[idx].enabled; }
    else {
      const defaults = INDICATOR_DEFAULTS[type];
      activeIndicators.push({ type, period: defaults.period, color: defaults.color, enabled: true,
        ...(type === "MACD" ? { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } : {}),
        ...(type === "BB" ? { stdDevMultiplier: 2 } : {}),
      });
      activeIndicators = [...activeIndicators];
    }
  }

  function updateIndicatorPeriod(type: IndicatorType, period: number) {
    const ind = activeIndicators.find((i) => i.type === type);
    if (ind) { ind.period = period; activeIndicators = [...activeIndicators]; }
  }

  function getChunkedRanges(start: number, end: number, chunkSize: number) {
    const ranges = [];
    for (let i = start; i < end; i += chunkSize) ranges.push({ lower_tick: i, upper_tick: Math.min(i + chunkSize, end) });
    return ranges;
  }

  async function fetchTrades() {
    if (isLoading) return;
    errorMsg = "";
    if (rangeExceeded) { errorMsg = `Range exceeds max (~${Math.round((MAX_SLOT_RANGE * SLOT_DURATION_MS) / 60_000)} min). Narrow your date range.`; return; }
    if (computedStartSlot >= computedEndSlot) { errorMsg = "Start must be before end."; return; }
    isLoading = true;
    tradePage = 0;
    trades = [];
    const poolInfo = await STORE.bot.sailfish.fetchPoolInfo(poolAddress);
    if (poolInfo instanceof Error) { errorMsg = (poolInfo as Error).message; isLoading = false; return; }
    const ranges = getChunkedRanges(computedStartSlot, computedEndSlot, 1_000);
    fetchTotal = ranges.length;
    fetchProgress = 0;
    for (const range of ranges) {
      currentFetchingRange = range;
      fetchProgress++;
      try {
        const result = await api.fetchTrades({ lower_tick: range.lower_tick, upper_tick: range.upper_tick, pool_types: ALL_POOL_TYPES, pool_addresses: [poolAddress], token_addresses: [], to_wallets: [], from_wallets: [] });
        const newTrades = result[poolAddress] ?? [];
        trades = [...trades, ...newTrades];
      } catch (e: any) { errorMsg = e.message ?? "Failed to fetch trades"; isLoading = false; return; }
      await new Promise((r) => setTimeout(r, 500));
    }
    isLoading = false;
    currentFetchingRange = null;
  }

  const formatPrice = (p: number) => { if (p >= 1) return p.toFixed(4); if (p >= 0.001) return p.toFixed(6); return p.toFixed(9); };
  const formatVol = (v: number) => { if (v >= 1000) return `${(v / 1000).toFixed(1)}K`; return v.toFixed(2); };

  function downloadTradesCsv() {
    const header = "slot,time,price,quote_amount,base_amount,fee,bribe,from_wallet,to_wallet,pool_address,tx_hash";
    const rows = sortedTrades.map((t) =>
      [t.index.tick, (t.index as any).tick_time ?? "", t.price, t.quote_amount, t.base_amount, t.fee, t.bribe, t.from_wallet, t.to_wallet, t.pool_address, t.index.tx_hash].join(",")
    );
    const blob = new Blob([header + "\n" + rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades_${poolAddress.slice(0, 8)}_${trades.length}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
</script>

<div class="px-4">
  <!-- Query Form -->
  <div class="max-w-2xl mx-auto mb-8">
    <div class="bg-gray-950/40 border border-gray-800 rounded-sm p-5">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-4">Query Parameters</p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="text-[10px] font-mono text-gray-500 uppercase tracking-widest block mb-1">Start</label>
          <input type="datetime-local" class="input input-sm bg-black border-gray-800 text-white font-mono text-sm w-full rounded-sm" bind:value={startDate} />
        </div>
        <div>
          <label class="text-[10px] font-mono text-gray-500 uppercase tracking-widest block mb-1">End</label>
          <input type="datetime-local" class="input input-sm bg-black border-gray-800 text-white font-mono text-sm w-full rounded-sm" bind:value={endDate} />
        </div>
      </div>
      {#if computedStartSlot > 0 && computedEndSlot > 0}
        <div class="flex flex-wrap items-center gap-2 mb-4">
          <span class="badge badge-outline rounded-none font-mono text-[10px] {rangeExceeded ? 'text-red-500 border-red-500/50' : 'text-gray-500 border-gray-700'} p-2">
            Slots: {computedStartSlot.toLocaleString()} → {computedEndSlot.toLocaleString()}
          </span>
          <span class="badge badge-outline rounded-none font-mono text-[10px] {rangeExceeded ? 'text-red-500 border-red-500/50' : 'text-gray-500 border-gray-700'} p-2">
            ~{Math.round((slotRange * SLOT_DURATION_MS) / 60_000)} min
          </span>
        </div>
      {/if}
      <div class="mb-4">
        <label class="text-[10px] font-mono text-gray-500 uppercase tracking-widest block mb-1">
          Pool Address
          <input type="text" class="input input-sm bg-black border-gray-800 text-white font-mono text-sm w-full rounded-sm mt-1" bind:value={poolAddress} />
        </label>
      </div>
      {#if errorMsg}<div class="text-red-500 text-xs font-mono mb-3">{errorMsg}</div>{/if}
      <button class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold text-xs transition-colors w-full" onclick={fetchTrades} disabled={isLoading}>
        {isLoading ? "Fetching..." : "Fetch Trades"}
      </button>
    </div>
  </div>

  {#if isLoading && currentFetchingRange}
    <div class="mb-6">
      <div class="flex items-center justify-between mb-2">
        <span class="text-[10px] font-mono text-sky-500 uppercase tracking-widest">
          Fetching batch {fetchProgress} / {fetchTotal}
        </span>
        <div class="flex items-center gap-3">
          {#if trades.length > 0}
            <button onclick={downloadTradesCsv} class="btn btn-xs rounded-sm bg-sky-600/10 border border-sky-600/20 text-sky-500 hover:bg-sky-600/20 font-mono text-[10px] uppercase tracking-widest transition-colors">
              Download CSV ({trades.length})
            </button>
          {/if}
          <span class="text-[10px] font-mono text-gray-600 tabular-nums">
            {((computedEndSlot - currentFetchingRange.upper_tick)).toLocaleString()} slots remaining
          </span>
        </div>
      </div>
      <div class="h-1 bg-gray-800 rounded-full overflow-hidden">
        <div class="h-full bg-sky-600 transition-all duration-300" style="width: {fetchTotal > 0 ? (fetchProgress / fetchTotal * 100) : 0}%"></div>
      </div>
    </div>
  {/if}

  {#if trades.length > 0}
    <div class="mb-4">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-2">Candle Interval</p>
      <div class="flex flex-wrap gap-1">
        {#each INTERVALS as int}
          <button class="btn btn-xs rounded-sm transition-colors {candleInterval === int.ms ? 'bg-sky-600 text-white border-sky-600' : 'bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'}" onclick={() => (candleInterval = int.ms)}>{int.label}</button>
        {/each}
      </div>
    </div>
    <div class="mb-6">
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-2">Indicators</p>
      <div class="flex flex-wrap items-center gap-2">
        {#each AVAILABLE_INDICATORS as ind}
          {@const config = activeIndicators.find((a) => a.type === ind.type)}
          {@const isActive = config?.enabled ?? false}
          <button class="btn btn-xs rounded-sm transition-colors {isActive ? 'text-white border-none' : 'bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700'}" style={isActive ? `background-color: ${INDICATOR_DEFAULTS[ind.type].color}` : ""} onclick={() => toggleIndicator(ind.type)}>{ind.label}</button>
          {#if isActive && config}
            <input type="number" class="input input-xs bg-black border-gray-800 text-white font-mono w-14 rounded-sm" value={config.period} min="2" max="200" oninput={(e) => updateIndicatorPeriod(ind.type, parseInt((e.target as HTMLInputElement).value) || config.period)} />
          {/if}
        {/each}
      </div>
    </div>
    <div class="mb-6"><CandleChart candles={historicCandles} indicators={indicatorResults} placeholder="Not enough trades to build candles..." /></div>
    {#if historicCandles.length > 0}
      {@const totalVol = historicCandles.reduce((a, c) => a + c.volume, 0)}
      {@const totalTx = historicCandles.reduce((a, c) => a + c.trades, 0)}
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Candles</p>
          <p class="text-lg font-black text-white tabular-nums">{historicCandles.length}</p>
        </div>
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Volume</p>
          <p class="text-lg font-black text-white tabular-nums">{formatVol(totalVol)}</p>
        </div>
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Trades</p>
          <p class="text-lg font-black text-white tabular-nums">{totalTx}</p>
        </div>
      </div>
    {/if}
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest">Trades <span class="text-gray-700">({trades.length})</span></p>
          <button onclick={downloadTradesCsv} class="btn btn-xs rounded-sm bg-sky-600/10 border border-sky-600/20 text-sky-500 hover:bg-sky-600/20 font-mono text-[10px] uppercase tracking-widest transition-colors">
            Download CSV
          </button>
        </div>
        {#if totalTradePages > 1}
          <div class="flex items-center gap-2">
            <button class="btn btn-xs rounded-sm bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-colors" disabled={tradePage === 0} onclick={() => tradePage--}>&lsaquo;</button>
            <span class="text-[10px] font-mono text-gray-500 tabular-nums">{tradePage + 1} / {totalTradePages}</span>
            <button class="btn btn-xs rounded-sm bg-gray-900/50 border border-gray-800 text-gray-400 hover:text-white hover:border-gray-700 transition-colors" disabled={tradePage >= totalTradePages - 1} onclick={() => tradePage++}>&rsaquo;</button>
          </div>
        {/if}
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-xs font-mono">
          <thead><tr class="text-gray-600 text-left border-b border-gray-800"><th class="py-1.5 px-2 font-normal">Age</th><th class="py-1.5 px-2 font-normal text-right">Price</th><th class="py-1.5 px-2 font-normal text-right">Quote</th><th class="py-1.5 px-2 font-normal text-right hidden sm:table-cell">Base</th><th class="py-1.5 px-2 font-normal text-right hidden md:table-cell">Wallet</th><th class="py-1.5 px-2 font-normal text-right">Tx</th></tr></thead>
          <tbody>
            {#each pagedTrades as trade (trade.index.tx_hash + trade.index.index_a)}
              <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
                <td class="py-1.5 px-2 text-gray-500 tabular-nums">{timeAgo((trade.index as any).tick_time)}</td>
                <td class="py-1.5 px-2 text-right text-white tabular-nums">{formatPrice(parseFloat(trade.price) || 0)}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums">{Math.abs(parseFloat(trade.quote_amount) || 0).toFixed(4)}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums hidden sm:table-cell">{Math.abs(parseFloat(trade.base_amount) || 0).toFixed(4)}</td>
                <td class="py-1.5 px-2 text-right hidden md:table-cell"><a href={`https://solscan.io/account/${trade.from_wallet}`} target="_blank" class="text-gray-500 hover:text-white transition-colors">{shortAddress(trade.from_wallet, 4, 3)}</a></td>
                <td class="py-1.5 px-2 text-right"><a href={`https://solscan.io/tx/${trade.index.tx_hash}`} target="_blank" class="text-sky-500/70 hover:text-sky-500 transition-colors">View</a></td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {:else if !isLoading && currentSlot > 0}
    <div class="text-center py-12"><p class="text-gray-600 text-sm">Set a date range and pool address, then click Fetch.</p></div>
  {/if}
</div>
